import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import { CSSProperties } from "@material-ui/styles";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CustomerListUrlSortField } from "@saleor/customers/urls";
import {
  getUserName,
  maybe,
  renderCollection,
  splitDateTime
} from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage } from "react-intl";

import { ListCustomers_customers_edges_node } from "../../types/ListCustomers";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      [theme.breakpoints.up("lg")]: {
        colEmail: {
          width: 200
        },
        colName: {},
        colOrders: {
          width: 80
        },
        colRegisterSource: {
          width: 200
        },
        colRegisterSite: {
          width: 200
        },
        colLastIpCountry: {
          width: 200
        },
        colFromReferral: {
          width: 200
        }
      },
      colEmail: {
        textAlign: "left",
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colRegisterSource: overflowing,
      colName: {
        paddingLeft: 0,
        overflow: "hidden", // 超出的文本隐藏
        textOverflow: "ellipsis", // 溢出用省略号显示
        whiteSpace: "nowrap" // 溢出不换行
      },
      colOrders: {
        textAlign: "center"
      },
      tableRow: {
        cursor: "pointer"
      }
    };
  },

  { name: "CustomerList" }
);

export interface CustomerListProps
  extends ListProps,
    ListActions,
    SortPage<CustomerListUrlSortField> {
  customers: ListCustomers_customers_edges_node[];
}

const numberOfColumns = 8;

const CustomerList: React.FC<CustomerListProps> = props => {
  const {
    settings,
    disabled,
    customers,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort,
    toolbar,
    toggle,
    toggleAll,
    selected,
    sort,
    isChecked
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={customers}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
        // direction={
        //   sort.sort === CustomerListUrlSortField.name
        //     ? getArrowDirection(sort.asc)
        //     : undefined
        // }
        // arrowPosition="right"
        // onClick={() => onSort(CustomerListUrlSortField.name)}
        // className={classes.colName}
        >
          <FormattedMessage defaultMessage="Customer Name" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === CustomerListUrlSortField.dateJoined
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CustomerListUrlSortField.dateJoined)}
        >
          <FormattedMessage defaultMessage="Joined Date" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === CustomerListUrlSortField.email
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CustomerListUrlSortField.email)}
          className={classes.colEmail}
        >
          <FormattedMessage defaultMessage="Customer Email" />
        </TableCellHeader>
        <TableCellHeader className={classes.colRegisterSite}>
          <FormattedMessage defaultMessage="Register Site" />
        </TableCellHeader>
        <TableCellHeader className={classes.colRegisterSource}>
          <FormattedMessage defaultMessage="Register Source" />
        </TableCellHeader>
        <TableCellHeader className={classes.colFromReferral}>
          <FormattedMessage defaultMessage="From referral" />
        </TableCellHeader>
        <TableCellHeader className={classes.colLastIpCountry}>
          <FormattedMessage defaultMessage="Last Ip Country" />
        </TableCellHeader>
        <TableCellHeader
          direction={
            sort.sort === CustomerListUrlSortField.orders
              ? getArrowDirection(sort.asc)
              : undefined
          }
          textAlign="center"
          onClick={() => onSort(CustomerListUrlSortField.orders)}
          className={classes.colOrders}
        >
          <FormattedMessage defaultMessage="No. of Orders" />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          customers,
          customer => {
            const isSelected = customer ? isChecked(customer.id) : false;

            return (
              <TableRow
                className={!!customer ? classes.tableRow : undefined}
                hover={!!customer}
                key={customer ? customer.id : "skeleton"}
                selected={isSelected}
                onClick={customer ? onRowClick(customer.id) : undefined}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(customer.id)}
                  />
                </TableCell>
                <TableCell className={classes.colName}>
                  {getUserName(customer)}
                </TableCell>
                <TableCell className={classes.colDate}>
                  {customer && customer.dateJoined ? (
                    splitDateTime(maybe(() => customer.dateJoined, "")).date +
                    " " +
                    splitDateTime(maybe(() => customer.dateJoined, "")).time
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colEmail}>
                  {maybe(() => customer) ? (
                    <p
                      title={customer.email}
                      className={classes.colEmail}
                      style={{ width: "100%" }}
                    >
                      {customer.email}
                    </p>
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colRegisterSite}>
                  {maybe<React.ReactNode>(
                    () => customer.registerSite,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colRegisterSource}>
                  {maybe<React.ReactNode>(
                    () => customer.registerSource,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colFromReferral}>
                  {maybe<React.ReactNode>(
                    () => customer.fromReferral,
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colLastIpCountry}>
                  {maybe<React.ReactNode>(
                    () => customer.lastIpCountry,
                    <Skeleton />
                  )}
                </TableCell>

                <TableCell className={classes.colOrders}>
                  {maybe<React.ReactNode>(
                    () => customer.orders.totalCount,
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No customers found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
CustomerList.displayName = "CustomerList";
export default CustomerList;
